import {
  Button,
  ButtonGroup,
  Content,
  Dialog,
  // Divider,
  Form,
  // Heading,
  Text,
  TextField,
  useDialogContainer,
  Tabs,
  TabList,
  TabPanels,
  Item,
  Flex,
  TextArea,
  Checkbox,
  ComboBox,
  useAsyncList,
  Switch,
  Tooltip,
  TooltipTrigger,
  ActionButton,
} from "@adobe/react-spectrum";
import { useOktaAuth } from "@okta/okta-react";
import React, { useEffect, useState } from "react";
import InfoOutline from "@spectrum-icons/workflow/InfoOutline";
import {
  error as ErrorToast,
  success as SuccessToast,
} from "@react/react-spectrum/Toast";
import PropTypes from "prop-types";
import useUserProfile from "../../../../context/user-context";
// import LoadingDialog from "../../LoadingDialog";
import { gtmhApi } from "../../../../api/gtmhApi";

export default function ApproveRejectedSubDialog({
  rowInfo,
  status,
  refreshTable,
  isPageLoading,
  setPageLoading,
}) {
  const dialog = useDialogContainer();
  const { user } = useUserProfile();
  const { authState } = useOktaAuth();

  // const [isPageLoading, setPageLoading] = useState(false);
  // const [searchInput, setSearchInput] = useState("");
  const [createType, setCreateType] = useState("parent");
  const [updateRequest, setUpdateRequest] = useState({
    sub_id: rowInfo?.sub_id,
    sub_name: rowInfo?.sub_name,
    duns_number: rowInfo?.duns_number,
    domain: rowInfo?.domain,
    parent_id: rowInfo?.parent_id || rowInfo?.sub_id,
    government: rowInfo?.government,
    diversified: rowInfo?.diversified, // parent creation
    sanctioned: rowInfo?.sanctioned, // sub creation
    comment: "",
    user_id: user?.userId,
    user_name: user?.name,
    status: rowInfo?.status,
    // status: "rejected",
  });

  // const [errorMessage, setErrorMessage] = useState("");
  const [errorObj, setErrorObj] = useState({});
  const [isIdSearch, setIsIdSearch] = useState(false);
  const [subName, setSubName] = useState(rowInfo?.sub_name);
  const [validNameError, setValidNameError] = useState(null);

  const tabs = rowInfo.parent
    ? [{ id: "parent", name: `Edit Parent: ${rowInfo.parent_name}` }]
    : [{ id: "sub", name: `Edit Sub: ${rowInfo.subsidiary_name}` }];

  const targetAccountList = useAsyncList({
    async load({ filterText }) {
      if (!filterText)
        return {
          items: [],
        };
      const json = await gtmhApi.searchParents(
        {
          search_string: isIdSearch ? "" : filterText,
          sub_id: !isIdSearch ? "" : filterText,
          status: status === "active" ? ["active"] : ["active", "in-review"],
          parent_only: true,
        },
        authState.accessToken
      );
      const results = json?.results[0].results || [];
      return {
        items: results,
      };
    },
  });

  useEffect(() => {
    targetAccountList.setFilterText(rowInfo.parent_name);
  }, []);

  const handleUpdate = () => {
    setPageLoading(true);
    gtmhApi
      .approveSub(updateRequest, authState.accessToken)
      .then((data) => {
        if (data.results?.sub_id) {
          dialog.dismiss();
          refreshTable();
          setPageLoading(false);
          SuccessToast(
            `Successfully ${
              rowInfo?.status === "rejected" ? "approved" : "restored"
            } ${updateRequest.sub_id}!`,
            {
              timeout: 5000,
            }
          );
        } else {
          setPageLoading(false);
          // setErrorMessage(data.results?.error_payload.message);
          const errorList = JSON.parse(
            data.results?.error_payload.error_description
          );
          const errorObject = {};
          errorList.forEach((err) => {
            errorObject[err.attribute] = err.message;
          });
          setErrorObj(errorObject);
        }
      })
      .catch((error) => {
        console.log("error");
        dialog.dismiss();
        setPageLoading(false);
        ErrorToast(error.response?.data?.message || "Error updating sub", {
          timeout: 5000,
        });
      });
  };

  const handleCancel = () => {
    dialog.dismiss();
  };

  const checkIsValidName = (name) => {
    setPageLoading(true);
    if (name === rowInfo?.sub_name) {
      setValidNameError(null);
      setPageLoading(false);
      return true;
    }
    gtmhApi
      .fetchParentSubs(
        {
          sub_name: name,
          status: [
            "active",
            "in-review",
            "rejected",
            "deleted",
            "pending-deletion",
          ],
          page_no: 1,
          page_size: 10,
          exact_match: true,
          sort_options: [{ field_name: "parent_id", sort_order: 1 }],
        },
        authState.accessToken
      )
      .then((data) => {
        if (data.results.results.length === 0) {
          setValidNameError(null);
          setPageLoading(false);
          return true;
        }
        const existingSub = data.results?.results[0]?.subs.find(
          (x) => x.sub_name === name
        );
        setValidNameError(
          `There is an existing sub with the same name (${existingSub?.sub_id} - status: ${existingSub?.status})`
        );
        setPageLoading(false);
        return false;
      })
      .catch((error) => {
        console.log("error: ", error);
        setPageLoading(false);
        return false;
      });
    // setPageLoading(false);
    return false;
  };

  const renderEditParentForm = () => (
    <Flex direction="column" width="100%">
      {/* <LoadingDialog isOpen={isPageLoading} /> */}
      {/* {errorMessage !== "" && (
        <Text UNSAFE_style={{ color: "red" }}>{errorMessage}</Text>
      )} */}
      <Form
        labelPosition="side"
        width="100%"
        marginTop="size-10"
        gap="10px"
        validationBehavior="native"
        validationErrors={errorObj}
      >
        <TextField
          autoFocus
          label="Name"
          name="sub_name"
          labelAlign="end"
          onChange={(val) => {
            setSubName(val);
            setUpdateRequest({ ...updateRequest, sub_name: val });
          }}
          onFocusChange={() => checkIsValidName(subName)}
          value={subName}
          isRequired
          validationState={validNameError ? "invalid" : undefined}
          errorMessage={validNameError}
        />
        <TextField
          label="Domain"
          name="domain"
          labelAlign="end"
          defaultValue={rowInfo.domain}
          onChange={(val) =>
            setUpdateRequest({ ...updateRequest, domain: val })
          }
          isRequired
        />
        <TextField
          label="HQ DUNS"
          name="duns_number"
          labelAlign="end"
          defaultValue={rowInfo.duns_number}
          onChange={(val) =>
            setUpdateRequest({ ...updateRequest, duns_number: val })
          }
          isRequired
        />
        <TextArea
          label="Comment"
          labelAlign="end"
          onChange={(val) =>
            setUpdateRequest({ ...updateRequest, comment: val })
          }
        />
      </Form>
      <Flex direction="column" marginStart="170px" marginTop="10px">
        <Checkbox
          defaultSelected={rowInfo.diversified}
          onChange={(val) =>
            setUpdateRequest({ ...updateRequest, diversified: val })
          }
        >
          Diversified Account
        </Checkbox>
      </Flex>
    </Flex>
  );

  const renderEditSubForm = () => (
    <Flex direction="column" width="100%">
      {/* {errorMessage !== "" && (
        <Text UNSAFE_style={{ color: "red" }}>{errorMessage}</Text>
      )} */}
      <Form
        labelPosition="side"
        width="100%"
        marginTop="size-10"
        gap="10px"
        validationBehavior="native"
        validationErrors={errorObj}
      >
        <TextField
          autoFocus
          label="Name"
          name="sub_name"
          labelAlign="end"
          onChange={(val) => {
            setSubName(val);
            setUpdateRequest({ ...updateRequest, sub_name: val });
          }}
          onFocusChange={() => checkIsValidName(subName)}
          value={subName}
          isRequired
          validationState={validNameError ? "invalid" : undefined}
          errorMessage={validNameError}
        />
        <TextField
          label="Domain"
          name="domain"
          labelAlign="end"
          defaultValue={rowInfo.domain}
          onChange={(val) =>
            setUpdateRequest({ ...updateRequest, domain: val })
          }
          isRequired
          //   validationState={isValidDuns}
          //   errorMessage="Please input a valid duns number (numeric &amp; <= 11 digits)"
        />
        <TextField
          label="HQ DUNS"
          name="duns_number"
          labelAlign="end"
          defaultValue={rowInfo.duns_number}
          onChange={(val) =>
            setUpdateRequest({ ...updateRequest, duns_number: val })
          }
          isRequired
          //   validationState={isValidDuns}
          //   errorMessage="Please input a valid duns number (numeric &amp; <= 11 digits)"
        />
        <ComboBox
          label="Parent"
          labelAlign="end"
          isRequired
          inputValue={targetAccountList.filterText}
          selectedKey={updateRequest.parent_id || null}
          onInputChange={(val) => {
            targetAccountList.setFilterText(val);
            // If user starts typing, clear selected parent
            setUpdateRequest({ ...updateRequest, parent_id: "" });
          }}
          onSelectionChange={(val) => {
            const selected = targetAccountList.items.find(
              (item) => item.sub_id === val
            );
            if (selected) {
              setUpdateRequest({
                ...updateRequest,
                parent_id: val.substring(0, val.length - 3),
              });
              targetAccountList.setFilterText(selected.sub_name); // show display name
            }
          }}
          items={targetAccountList.items}
          loadingState={targetAccountList.loadingState}
          description={
            <Flex marginTop="-2px" marginBottom="-15px">
              <Flex height="32px" alignItems="center">
                <Switch
                  isSelected={isIdSearch}
                  onChange={setIsIdSearch}
                  margin={0}
                >
                  <Text UNSAFE_style={{ fontSize: "12px" }}>ID Search</Text>
                </Switch>
                <TooltipTrigger>
                  <ActionButton
                    isQuiet
                    aria-label="Information"
                    height="20px"
                    marginStart="-12px"
                  >
                    <InfoOutline size="S" />
                  </ActionButton>
                  <Tooltip>
                    ID search is an exact search returning only the id and
                    associated parent/subs which match the input
                  </Tooltip>
                </TooltipTrigger>
              </Flex>
            </Flex>
          }
        >
          {(item) =>
            item && (
              <Item key={item.sub_id}>
                <Text>{item.sub_name}</Text>
              </Item>
            )
          }
        </ComboBox>
        <TextArea
          autoFocus
          label="Comment"
          labelAlign="end"
          onChange={(val) =>
            setUpdateRequest({ ...updateRequest, comment: val })
          }
        />
      </Form>
      <Flex direction="column" marginStart="152px" marginTop="10px">
        <Checkbox
          defaultSelected={rowInfo.sanctioned}
          onChange={(val) =>
            setUpdateRequest({ ...updateRequest, sanctioned: val })
          }
        >
          Sanctioned Account
        </Checkbox>
      </Flex>
    </Flex>
  );

  return (
    //   <Dialog height="295px">
    <Dialog size="L" width="50rem" height="100%">
      <Content>
        <Flex direction="column">
          <Tabs
            aria-label="Parent + Subsidiary Accounts"
            margin="size-100"
            marginTop="size-0"
            width="90%"
            direction="row"
            items={tabs}
            defaultSelectedKey={createType}
            onSelectionChange={(val) => setCreateType(val)}
          >
            <TabList flex="1 1 auto">
              {(item) => <Item>{item.name}</Item>}
            </TabList>
            <TabPanels>
              {(item) => (
                <Item id={item.id}>
                  <Flex
                    marginTop="size-50"
                    UNSAFE_style={{
                      backgroundColor: "white",
                      borderRadius: "0.5rem",
                    }}
                    direction="row"
                    // height={
                    //   list.items > 0 ? "static-size-7000" : "static-size-6000"
                    // }
                  >
                    {item.id === "parent"
                      ? renderEditParentForm()
                      : renderEditSubForm()}
                  </Flex>
                </Item>
              )}
            </TabPanels>
          </Tabs>
          <Flex direction="column" alignItems="end">
            <ButtonGroup>
              <Button onPress={handleCancel} variant="secondary">
                Cancel
              </Button>
              <Button
                onPress={() => handleUpdate()}
                // {() =>
                //   createType === "parent" ? createParent() : createSub()
                // }
                color="primary"
                variant="accent"
                isDisabled={
                  updateRequest.name === "" ||
                  updateRequest.duns_number === "" ||
                  updateRequest.domain === "" ||
                  (createType === "sub" && updateRequest.parent_id === "") ||
                  validNameError !== null ||
                  isPageLoading
                }
              >
                {rowInfo?.status === "rejected" ? "Approve" : "Restore"}
              </Button>
            </ButtonGroup>
          </Flex>
        </Flex>
      </Content>
    </Dialog>
  );
}

ApproveRejectedSubDialog.propTypes = {
  status: PropTypes.string.isRequired,
  // handleSave: PropTypes.func.isRequired,
  refreshTable: PropTypes.func.isRequired,
  isPageLoading: PropTypes.bool.isRequired,
  setPageLoading: PropTypes.func.isRequired,
  rowInfo: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
      PropTypes.arrayOf(PropTypes.object),
    ])
  ).isRequired,
};
